import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import BarRestaurantOrderCardCustom from './BarRestaurantOrderCardCustom';
import BarRestaurantOrderCard from './BarRestaurantOrderCard';
import BarRestaurantOrderDetailModal from './BarRestaurantOrderDetailModal';
import BarRestaurantTableDetailModal from './BarRestaurantTableDetailModal';
import './BarRestaurantListFullscreenCustom.css';
import AppIcon from './icons/AppIcon';

export default function BarRestaurantListFullscreenCustom({ 
  onClose, 
  projectId,
  allOrders
}) {
  const [localOrders, setLocalOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [availableLists, setAvailableLists] = useState([]);
  const [selectedListIds, setSelectedListIds] = useState([]);
  const [showListSelector, setShowListSelector] = useState(false);
  const [listNamesMap, setListNamesMap] = useState({});
  
  // Carregar configuração do localStorage
  function loadConfigFromStorage() {
    try {
      const config = JSON.parse(localStorage.getItem('bar_restaurant_fullscreen_custom_config') || '{}');
      return config.selectedListIds || [];
    } catch {
      return [];
    }
  }
  
  // Salvar configuração no localStorage
  function saveConfigToStorage(listIds) {
    const config = {
      projectId,
      selectedListIds: listIds,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('bar_restaurant_fullscreen_custom_config', JSON.stringify(config));
  }

  // Carregar listas disponíveis do projeto (apenas listas de pedidos)
  useEffect(() => {
    const loadAvailableLists = async () => {
      try {
        const { data, error } = await supabase
          .from('project_lists')
          .select('id, name')
          .eq('project_id', projectId)
          .not('name', 'ilike', '%Mesa%')
          .order('display_order', { ascending: true });

        if (error) throw error;

        const namesMap = {};
        data.forEach(list => {
          namesMap[list.id] = list.name;
        });
        
        setAvailableLists(data || []);
        setListNamesMap(namesMap);

        // Carregar configuração salva
        const savedListIds = loadConfigFromStorage();
        if (savedListIds.length > 0) {
          setSelectedListIds(savedListIds);
        }
      } catch (error) {
        console.error('[BarRestaurantListFullscreenCustom] Erro ao carregar listas:', error);
      }
    };

    if (projectId) {
      loadAvailableLists();
    }
  }, [projectId]);

  // Filtrar pedidos baseado nas listas selecionadas
  useEffect(() => {
    if (selectedListIds.length === 0) {
      setLocalOrders([]);
      return;
    }

    // Mapa de status para list_id
    const statusToListIdMap = {
      'pedidos_em_aberto': 'ab18bae2-605d-406a-9fbd-b6d772e44de8',
      'em_producao': 'bbd7866e-bbe4-47bc-a502-2339c7e09e93',
      'pronto': '5cc493b4-f667-4f8a-a586-eca224192b65',
      'entregue': 'c0060625-d0bc-4bde-9932-d8ee2448d4f9'
    };

    const filtered = allOrders.filter(order => {
      const listId = statusToListIdMap[order.status_atual];
      return listId && selectedListIds.includes(listId);
    });

    // Ordenar por created_at (mais antigo primeiro)
    const sorted = filtered.sort((a, b) => {
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      return dateA - dateB;
    });

    console.log('[BarRestaurantListFullscreenCustom] Filtrando pedidos:', {
      selectedListIds,
      totalOrders: allOrders.length,
      filteredOrders: sorted.length,
      filtered: sorted
    });

    setLocalOrders(sorted);
  }, [selectedListIds, allOrders]);

  // Real-time subscription para atualizações
  useEffect(() => {
    if (!projectId || selectedListIds.length === 0) return;

    console.log(`[BarRestaurantListFullscreenCustom] Configurando Real-time para listas: ${selectedListIds.join(', ')}`);

    const statusToListIdMap = {
      'pedidos_em_aberto': 'ab18bae2-605d-406a-9fbd-b6d772e44de8',
      'em_producao': 'bbd7866e-bbe4-47bc-a502-2339c7e09e93',
      'pronto': '5cc493b4-f667-4f8a-a586-eca224192b65',
      'entregue': 'c0060625-d0bc-4bde-9932-d8ee2448d4f9'
    };

    const ordersChannel = supabase
      .channel(`bar_restaurant_orders_custom:${projectId}:${selectedListIds.join('-')}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bar_restaurant_orders',
          filter: `project_id=eq.${projectId}`
        },
        (payload) => {
          console.log(`[BarRestaurantListFullscreenCustom] Real-time - Pedido alterado:`, payload);
          
          const listId = statusToListIdMap[payload.new?.status_atual || payload.old?.status_atual];
          const isInSelectedLists = listId && selectedListIds.includes(listId);

          if (payload.eventType === 'INSERT') {
            if (isInSelectedLists) {
              setLocalOrders(prev => [...prev, payload.new]);
            }
          } else if (payload.eventType === 'UPDATE') {
            if (isInSelectedLists) {
              setLocalOrders(prev => {
                const exists = prev.find(o => o.id === payload.new.id);
                if (exists) {
                  return prev.map(o => o.id === payload.new.id ? payload.new : o);
                }
                return [...prev, payload.new];
              });
            } else {
              setLocalOrders(prev => prev.filter(o => o.id !== payload.new.id));
            }
          } else if (payload.eventType === 'DELETE') {
            setLocalOrders(prev => prev.filter(o => o.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      console.log(`[BarRestaurantListFullscreenCustom] Desconectando Real-time`);
      supabase.removeChannel(ordersChannel);
    };
  }, [projectId, selectedListIds]);

  // ESC para fechar
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleOpenOrder = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedOrder(null);
  };

  const handleOpenTable = async (order) => {
    try {
      const response = await fetch(`/api/bar-restaurant/order/${order.id}/table`);
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Erro ao buscar mesa');
      }

      setSelectedTable(data.table);
      setShowTableModal(true);
    } catch (error) {
      console.error('[BarRestaurantListFullscreenCustom] Erro ao abrir mesa:', error);
      alert('Erro ao abrir mesa: ' + error.message);
    }
  };

  const handleCloseTableModal = () => {
    setShowTableModal(false);
    setSelectedTable(null);
  };

  const handleAddLists = (newListIds) => {
    const combined = Array.from(new Set([...selectedListIds, ...newListIds]));
    setSelectedListIds(combined);
    saveConfigToStorage(combined);
    setShowListSelector(false);
  };

  const handleRemoveList = (listIdToRemove) => {
    const updated = selectedListIds.filter(id => id !== listIdToRemove);
    setSelectedListIds(updated);
    saveConfigToStorage(updated);
  };

  const selectedListNames = selectedListIds.map(id => listNamesMap[id]).filter(Boolean);

  return (
    <div className="fullscreen-overlay">
      <div className="fullscreen-container">
        {/* Header */}
        <div className="fullscreen-header">
          <div className="header-content">
            <h1>Tela Cheia Personalizada</h1>
            <div className="selected-lists">
              {selectedListNames.length > 0 ? (
                selectedListNames.map((name, idx) => (
                  <span key={idx} className="list-badge">
                    {name}
                    <button 
                      className="badge-remove"
                      onClick={() => handleRemoveList(selectedListIds[idx])}
                      title="Remover lista"
                    >
                      ×
                    </button>
                  </span>
                ))
              ) : (
                <span className="no-lists">Nenhuma lista selecionada</span>
              )}
            </div>
            <span className="card-count">{localOrders.length} pedido{localOrders.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="header-buttons">
            <button 
              className="btn-add-list" 
              onClick={() => setShowListSelector(true)}
              title="Adicionar lista"
            >
              <AppIcon name="add" />
            </button>
            <button 
              className="btn-close-fullscreen" 
              onClick={onClose}
              title="Fechar (ESC)"
            >
              <AppIcon name="close" />
            </button>
          </div>
        </div>

        {/* Grid de Cards */}
        <div className="fullscreen-grid">
          {localOrders.length === 0 ? (
            <div className="fullscreen-empty-state">
              <AppIcon name="receipt_long" />
              <p>{selectedListIds.length === 0 ? 'Selecione listas para visualizar pedidos' : 'Nenhum pedido nas listas selecionadas'}</p>
            </div>
          ) : (
            localOrders.map(order => (
              <div key={order.id} className="fullscreen-card-wrapper">
                <BarRestaurantOrderCardCustom 
                  order={order} 
                  onOpenCard={handleOpenOrder}
                  onOpenTable={handleOpenTable}
                  isDragging={false}
                />
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal de Seleção de Listas */}
      {showListSelector && (
        <div className="list-selector-overlay">
          <div className="list-selector-modal">
            <div className="selector-header">
              <h2>Selecionar Listas</h2>
              <button 
                className="btn-close-selector"
                onClick={() => setShowListSelector(false)}
              >
                <AppIcon name="close" />
              </button>
            </div>

            <div className="selector-content">
              {availableLists.map(list => (
                <label key={list.id} className="list-checkbox">
                  <input 
                    type="checkbox" 
                    checked={selectedListIds.includes(list.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleAddLists([list.id]);
                      } else {
                        handleRemoveList(list.id);
                      }
                    }}
                  />
                  <span>{list.name}</span>
                </label>
              ))}
            </div>

            <div className="selector-footer">
              <button 
                className="btn-close-modal"
                onClick={() => setShowListSelector(false)}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalhes */}
      {showModal && selectedOrder && (
        <BarRestaurantOrderDetailModal
          order={selectedOrder}
          onClose={handleCloseModal}
        />
      )}

      {showTableModal && selectedTable && (
        <BarRestaurantTableDetailModal
          table={selectedTable}
          onClose={handleCloseTableModal}
        />
      )}
    </div>
  );
}
